import { api, withQuietRequests } from "./axiosClient";
import type { AiClauseCompareRequest, AiClauseCompareResult } from "../types/clauseLibrary.types";

/**
 * Pactline AI (the Python lawsigna-ai service, /api/v1/ai/* via the gateway). Every call runs
 * server-side under the signed-in user's own permissions; no provider key or
 * model call ever happens in the browser.
 *
 * AI calls are slow (seconds to minutes), so they get a longer timeout and
 * skip the full-screen loader — each AI surface shows its own progress.
 */
// Just above the gateway's 600 s limit for AI routes, so the gateway answers first.
const AI_TIMEOUT_MS = 610_000;

/**
 * Base URL for every AI call: VITE_AI_API_BASE_URL (e.g. the gateway, or the
 * AI service itself at http://localhost:8000/api/v1), else the main API base.
 * Requests still go through the shared client, so the user's token and the
 * usual 401/402/403 handling apply.
 */
export const AI_API_BASE_URL: string =
  import.meta.env.VITE_AI_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;

export type AiConfidence = "high" | "medium" | "needs_review";
export type AiReviewStatus =
  | "pending"
  | "accepted"
  | "edited"
  | "rejected"
  | "ignored"
  | "flagged";
export type AiComparison =
  | "match"
  | "mismatch"
  | "new"
  | "not_comparable"
  | "not_found";
export type AiSummaryKind =
  | "executive"
  | "contract"
  | "key_terms"
  | "commercial"
  | "legal"
  | "risk";

export interface AiSource {
  chunkId?: string;
  section?: string | null;
  page?: number | null;
  quote?: string;
  /** The quote was found in the document text the AI was given. */
  verified: boolean;
}

export interface AiSourcedStatement {
  text: string;
  sources: AiSource[];
  /** "Source could not be confidently identified." when nothing verified. */
  sourceNotice?: string | null;
}

export interface AiSummaryField extends AiSourcedStatement {
  label: string;
  found: boolean;
}

export interface AiContractSummary {
  kind: AiSummaryKind;
  overview: AiSourcedStatement;
  fields: AiSummaryField[];
  keyObligations: AiSourcedStatement[];
  keyRisks: AiSourcedStatement[];
}

export interface AiContractAnswer extends AiSourcedStatement {
  question?: string;
  answerable: boolean;
  confidence: AiConfidence;
}

export interface AiMetadataField extends AiSourcedStatement {
  suggestionId: string;
  key: string;
  label: string;
  found: boolean;
  confidence: AiConfidence;
  existingValue?: string | null;
  comparison: AiComparison;
  reviewStatus: AiReviewStatus;
}

export interface AiDueRule {
  kind?: "fixed_date" | "relative" | "recurring" | "unspecified";
  date?: string | null;
  offsetValue?: number | null;
  offsetUnit?: string | null;
  anchor?: string | null;
  description?: string | null;
  isRelative: boolean;
}

export interface AiObligationSuggestion extends AiSourcedStatement {
  suggestionId: string;
  title: string;
  description: string;
  responsibleParty?: string | null;
  responsiblePartyNotice?: string | null;
  obligationType?: string | null;
  frequency: string;
  trigger?: string | null;
  dueRule?: AiDueRule | null;
  evidenceRequired?: string | null;
  consequence?: string | null;
  confidence: AiConfidence;
  reviewStatus: AiReviewStatus;
  createdObligationId?: string | null;
}

/** A suggested connection: the dependent obligation starts after the predecessor is completed. */
export interface AiDependencySuggestion extends AiSourcedStatement {
  suggestionId: string;
  predecessorSuggestionId: string;
  dependentSuggestionId: string;
  predecessorTitle: string;
  dependentTitle: string;
  type: "completion";
  offsetValue?: number | null;
  offsetUnit?: string | null;
  reviewStatus: AiReviewStatus;
}

export interface AiObligationSuggestions {
  obligations: AiObligationSuggestion[];
  dependencies?: AiDependencySuggestion[];
  partial: boolean;
}

// ----------------------------------------------------------------------
// Obligation Hub
// ----------------------------------------------------------------------

export type AiAlignmentStatus = "consistent" | "possible_mismatch" | "cannot_assess";

export interface AiObligationSummary {
  overview: AiSourcedStatement;
  fields: AiSummaryField[];
  alignment: AiSourcedStatement & { status: AiAlignmentStatus };
  /** False when the source clause could not be read (no contract access or no document). */
  clauseAvailable: boolean;
}

export type AiRiskSeverity = "high_attention" | "medium_attention" | "review" | "informational";

export interface AiRiskFinding {
  code: string;
  severity: AiRiskSeverity;
  issue: string;
  explanation: string;
  action: string;
  source: string;
}

/** Computed from the record and its connections by rules; no AI model is used. */
export interface AiObligationRisks {
  obligationId: string;
  findings: AiRiskFinding[];
  evaluatedAt: string;
  basis: string;
}

export interface AiObligationRef {
  id: string;
  number?: string | null;
  title?: string | null;
  dueDate?: string | null;
}

export interface AiInsight {
  code: string;
  severity: AiRiskSeverity;
  count: number;
  message: string;
  examples: AiObligationRef[];
}

export interface AiObligationInsights {
  insights: AiInsight[];
  obligationsConsidered: number;
  truncated: boolean;
  generatedAt: string;
  basis: string;
}

export interface AiEvidenceRequiredItem extends AiSourcedStatement {
  item: string;
  status: "present" | "missing" | "unclear";
}

export interface AiEvidenceField extends AiSourcedStatement {
  suggestionId: string;
  name: string;
  reviewStatus: AiReviewStatus;
}

export type AiTriState = "yes" | "no" | "unclear" | "not_applicable";

export interface AiEvidenceAnalysis {
  suggestionId: string;
  mediaId: string;
  fileName?: string | null;
  isImage: boolean;
  summary: string;
  documentType?: string | null;
  relevance: "likely_relevant" | "possibly_relevant" | "unlikely_relevant";
  relevanceExplanation?: string | null;
  periodCovered?: string | null;
  requiredInformation: AiEvidenceRequiredItem[];
  requiredDetected: number;
  requiredTotal: number;
  issues: (AiSourcedStatement & { issue: string })[];
  datesConsistent?: AiTriState | null;
  datesExplanation?: string | null;
  responsiblePartyMatches?: AiTriState | null;
  completeness?: "appears_complete" | "appears_incomplete" | "unclear" | null;
  /** A recommendation to the reviewer only; AI never verifies evidence. */
  recommendation: "accept" | "review" | "reject";
  recommendationReason?: string | null;
  extractedFields: AiEvidenceField[];
  confidence: AiConfidence;
  reviewStatus: AiReviewStatus;
  /** "AI does not verify evidence. Record the outcome in the obligation's Verify step." */
  notice: string;
}

// ----------------------------------------------------------------------
// Receivables Engine
// ----------------------------------------------------------------------

export interface AiReceivableSummary {
  overview: AiSourcedStatement;
  fields: AiSummaryField[];
  /** Considerations for the recovery owner, each grounded in a cited fact; never actions taken. */
  nextSteps: AiSourcedStatement[];
  contractAvailable: boolean;
}

/** Computed from the receivable's record by rules; no AI model is used. */
export interface AiReceivableRisks {
  entryId: string;
  findings: AiRiskFinding[];
  evaluatedAt: string;
  basis: string;
}

export interface AiAnalysis<TResult = unknown> {
  analysisId: string;
  operation: string;
  variant?: string;
  status: string;
  fromCache: boolean;
  createdAt: string;
  createdByName?: string;
  provider?: string;
  model?: string;
  promptVersion?: string;
  documentMediaId?: string | null;
  warnings: string[];
  /** "AI-generated analysis for review. It is not legal advice…" */
  notice: string;
  result: TResult | null;
}

export interface AiAnalysisListItem {
  analysisId: string;
  operation: string;
  variant?: string;
  status: string;
  createdAt: string;
  createdByName?: string;
  model?: string;
}

export interface AiStatus {
  enabled: boolean;
  providerConfigured: boolean;
  message?: string | null;
  documentAnalysisEnabled: boolean;
  contractRiskAnalysisEnabled: boolean;
  obligationAiEnabled: boolean;
  evidenceAiEnabled: boolean;
}

export interface AiSettings {
  aiEnabled: boolean;
  documentAnalysisEnabled: boolean;
  contractRiskAnalysisEnabled: boolean;
  obligationAiEnabled: boolean;
  evidenceAiEnabled: boolean;
  monthlyRequestLimit?: number | null;
  provider?: string | null;
  providerIsOrganisationConfigured: boolean;
  updatedByName?: string | null;
  updatedAt?: string | null;
}

export interface AiUsageSummary {
  from: string;
  to: string;
  requests: number;
  monthlyRequestLimit?: number | null;
  inputTokens: number;
  outputTokens: number;
  estimatedCost?: number | null;
}

export interface AiTemplateDraftRequest {
  templateName: string;
  category?: string | null;
  templateType: "contract" | "clause";
  instructions?: string | null;
  existingPlaceholders?: string[];
}

export interface AiTemplateDraft {
  text: string;
  placeholders: string[];
}

export type AiDecision = "accepted" | "edited" | "rejected" | "ignored" | "flagged";

/** A model the user may run AI with; "auto" lets each AI agent use the model routed for its task. */
export interface AiModelChoice {
  id: string;
  label: string;
}

export interface AiModelOptions {
  default: string;
  models: AiModelChoice[];
}

export const AI_MODEL_AUTO = "auto";

/** The chosen model as the request sends it: omitted for "auto", so the server routes by task. */
const chosen = (model?: string) => (model && model !== AI_MODEL_AUTO ? model : undefined);

// ----------------------------------------------------------------------
// AI Models & Keys (super admins): platform-wide provider accounts and models
// ----------------------------------------------------------------------

/** One AI agent as the service describes it (agents/list). */
export interface AiAgentInfo {
  /** The agent's name, e.g. "contract_summary"; work is assigned to it by this name. */
  name: string;
  title: string;
  /** One sentence: what the agent does. */
  role: string;
  /** The operation its results are stored under, e.g. "ContractSummary". */
  operation: string;
  /** Its active instructions version, e.g. "contract_summary@v1". */
  version: string;
  /** Whether it can take work for this organisation now (platform, provider and organisation switches). */
  enabled: boolean;
  /** JSON Schema of the work it accepts (camelCase properties). */
  input: { properties?: Record<string, { description?: string; type?: string; anyOf?: unknown[] }>; required?: string[] };
}

export type AiTier = "economy" | "standard" | "advanced";

export interface AiProviderType {
  id: string;
  label: string;
  needsBaseUrl: boolean;
}

/** A provider account. The key itself never comes back: only its last characters. */
export interface AiProviderCredential {
  id: string;
  provider: string;
  label: string;
  keyHint: string;
  baseUrl?: string | null;
  apiVersion?: string | null;
  isActive: boolean;
  /** False when the stored key cannot be decrypted (the service's encryption key changed). */
  keyReadable: boolean;
  updatedByName?: string | null;
  updatedAt?: string | null;
}

export interface AiModelConfig {
  id: string;
  provider: string;
  modelId: string;
  label: string;
  /** Offered in the model pickers beside AI actions. */
  selectable: boolean;
  /** The tier "Auto" routes to this model, if any. */
  tier?: AiTier | null;
  inputPrice?: number | null;
  outputPrice?: number | null;
  sortOrder: number;
  updatedByName?: string | null;
  updatedAt?: string | null;
}

export interface AiPlatformConfig {
  providerTypes: AiProviderType[];
  tiers: AiTier[];
  /** False until the AI service has INTELLIGENCE_ENCRYPTION_KEY: keys cannot be saved. */
  encryptionReady: boolean;
  active: {
    source: "admin" | "environment";
    provider: string;
    credentialId?: string | null;
    keyReadable: boolean;
    offeredModels: string[];
  };
  credentials: AiProviderCredential[];
  models: AiModelConfig[];
  suggestedModels: AiModelChoice[];
}

export interface AiProviderCredentialSave {
  id?: string;
  provider: string;
  label: string;
  /** Omit when editing to keep the stored key. */
  apiKey?: string;
  baseUrl?: string | null;
  apiVersion?: string | null;
}

export type AiModelConfigSave = Omit<AiModelConfig, "id" | "updatedByName" | "updatedAt"> & { id?: string };

const post = async <T>(url: string, body: unknown = {}): Promise<T> => {
  const response = await withQuietRequests(() =>
    api.post<T>(url, body, { baseURL: AI_API_BASE_URL, timeout: AI_TIMEOUT_MS }),
  );
  return response.data;
};

export const aiApi = {
  status: () => post<AiStatus>("/ai/status"),

  /** The models offered beside every AI action and in the assistant ("auto" first). */
  models: () => post<AiModelOptions>("/ai/models/list"),

  summarize: (contractId: string, kind: AiSummaryKind, force = false, model?: string) =>
    post<AiAnalysis<AiContractSummary>>("/ai/contract/summary", {
      contractId,
      kind,
      force,
      model: chosen(model),
    }),

  ask: (contractId: string, question: string, model?: string) =>
    post<AiAnalysis<AiContractAnswer>>("/ai/contract/ask", {
      contractId,
      question,
      model: chosen(model),
    }),

  extractMetadata: (contractId: string, force = false, model?: string) =>
    post<AiAnalysis<{ fields: AiMetadataField[] }>>(
      "/ai/contract/extractmetadata",
      { contractId, force, model: chosen(model) },
    ),

  suggestObligations: (contractId: string, force = false, model?: string) =>
    post<AiAnalysis<AiObligationSuggestions>>("/ai/contract/suggestobligations", {
      contractId,
      force,
      model: chosen(model),
    }),

  listAnalyses: (contractId: string, operation?: string) =>
    post<AiAnalysisListItem[]>("/ai/contract/analyses", {
      contractId,
      operation,
    }),

  // ---- Obligation Hub ----

  summarizeObligation: (obligationId: string, force = false, model?: string) =>
    post<AiAnalysis<AiObligationSummary>>("/ai/obligation/summary", { obligationId, force, model: chosen(model) }),

  askObligation: (obligationId: string, question: string, model?: string) =>
    post<AiAnalysis<AiContractAnswer>>("/ai/obligation/ask", { obligationId, question, model: chosen(model) }),

  analyzeEvidence: (obligationId: string, mediaId: string, force = false, model?: string) =>
    post<AiAnalysis<AiEvidenceAnalysis>>("/ai/obligation/analyzeevidence", {
      obligationId,
      mediaId,
      force,
      model: chosen(model),
    }),

  /** Rules only: instant, no model call, no credits. */
  obligationRisks: (obligationId: string) =>
    post<AiObligationRisks>("/ai/obligation/risks", { obligationId }),

  /** Rules only, across the obligations the user can access. */
  obligationInsights: () => post<AiObligationInsights>("/ai/obligation/insights"),

  listObligationAnalyses: (obligationId: string, operation?: string) =>
    post<AiAnalysisListItem[]>("/ai/obligation/analyses", { obligationId, operation }),

  // ---- Receivables Engine ----

  summarizeReceivable: (entryId: string, force = false, model?: string) =>
    post<AiAnalysis<AiReceivableSummary>>("/ai/receivable/summary", { entryId, force, model: chosen(model) }),

  askReceivable: (entryId: string, question: string, model?: string) =>
    post<AiAnalysis<AiContractAnswer>>("/ai/receivable/ask", { entryId, question, model: chosen(model) }),

  /** Rules only: instant, no model call, no credits. */
  receivableRisks: (entryId: string) =>
    post<AiReceivableRisks>("/ai/receivable/risks", { entryId }),

  listReceivableAnalyses: (entryId: string, operation?: string) =>
    post<AiAnalysisListItem[]>("/ai/receivable/analyses", { entryId, operation }),

  getAnalysis: <T>(analysisId: string) =>
    post<AiAnalysis<T>>("/ai/analysis/get", { analysisId }),

  decide: (
    suggestionId: string,
    decision: AiDecision,
    options: { finalValue?: unknown; createdEntityId?: string } = {},
  ) =>
    post<{ suggestionId: string; reviewStatus: AiReviewStatus; message: string }>(
      "/ai/suggestion/decide",
      { suggestionId, decision, ...options },
    ),

  getSettings: () => post<AiSettings>("/ai/settings/get"),
  saveSettings: (settings: AiSettings) =>
    post<AiSettings>("/ai/settings/save", settings),
  usage: () => post<AiUsageSummary>("/ai/usage/summary", {}),

  // ---- AI Models & Keys (super admins only; the server enforces it) ----

  /** Every AI agent, what it does and whether it can take work for this organisation. */
  agents: () => post<AiAgentInfo[]>("/ai/agents/list"),

  platformConfig: () => post<AiPlatformConfig>("/ai/platform/get"),
  saveProviderCredential: (body: AiProviderCredentialSave) =>
    post<AiPlatformConfig>("/ai/platform/providers/save", body),
  /** `null` goes back to the AI service's environment settings. */
  activateProviderCredential: (id: string | null) =>
    post<AiPlatformConfig>("/ai/platform/providers/activate", { id }),
  deleteProviderCredential: (id: string) => post<AiPlatformConfig>("/ai/platform/providers/delete", { id }),
  testProviderCredential: (id: string, model?: string) =>
    post<{ ok: boolean; message: string; model?: string }>("/ai/platform/providers/test", { id, model }),
  saveModelConfig: (body: AiModelConfigSave) => post<AiPlatformConfig>("/ai/platform/models/save", body),
  deleteModelConfig: (id: string) => post<AiPlatformConfig>("/ai/platform/models/delete", { id }),

  /** Drafts the starting wording of a contract or clause template; variable parts come back as [[Placeholder]]. */
  draftTemplate: (req: AiTemplateDraftRequest, model?: string) =>
    post<AiTemplateDraft>("/ai/template/draft", { ...req, model: chosen(model) }),

  /** Compares a contract's clause with a library clause and its fallback positions. */
  compareClause: (req: AiClauseCompareRequest, model?: string) =>
    post<{ result: AiClauseCompareResult }>("/ai/clause/compare", { ...req, model: chosen(model) }),
};

/**
 * The server's own message for an AI failure ("AI service is temporarily
 * unavailable.", "AI result requires review.", …), else the fallback.
 * 402/403 are already toasted by the shared client.
 */
export function aiErrorMessage(error: unknown, fallback: string): string {
  const e = error as {
    response?: { status?: number; data?: { message?: string } };
    code?: string;
  };
  if (e?.code === "ECONNABORTED" || !e?.response) {
    return "AI service is temporarily unavailable.";
  }
  return e.response.data?.message || fallback;
}

/** True when the shared client already showed a toast for this error. */
export function aiErrorAlreadyShown(error: unknown): boolean {
  const e = error as { isPermissionDenied?: boolean; isEntitlementDenied?: boolean };
  return Boolean(e?.isPermissionDenied || e?.isEntitlementDenied);
}
